import type { BrandVariants } from '@fluentui/react-components';

/**
 * Ramp generation — turning one chosen colour into the sixteen stops Fluent needs.
 *
 * `brand.ts` documents how the shipped orange ramp was built: convert Fluent's
 * own reference ramp to CIE L*C*h, then match its lightness progression at the
 * brand's hue, capping chroma to the sRGB gamut at each step. That was done by
 * hand, once. This module does the same thing at runtime, so a user can pick any
 * colour and get a ramp with the same perceptual structure.
 *
 * It is deliberately dependency-free and free of React: everything here is a
 * pure function over numbers, which is what makes `ramp.test.ts` able to check
 * the whole thing against known values.
 *
 * Colour space notes, since these are easy to get subtly wrong:
 *   • Lab/LCh here is CIE L*a*b* under a D65 white point, matching sRGB's own
 *     illuminant. Using D50 (the ICC default) would shift every hue slightly.
 *   • WCAG contrast uses *relative luminance*, which is a different transfer
 *     function from L*. Both live in this file; don't substitute one for the other.
 */

export type BrandStop = keyof BrandVariants;

export const BRAND_STOPS = [
  10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160,
] as const satisfies readonly BrandStop[];

/**
 * The lightness and chroma of Fluent's own `brandWeb` ramp (the `#0f6cbd`
 * family), in CIE L*C*h.
 *
 * Baked in as numbers rather than imported: `brandWeb` lives at
 * `@fluentui/tokens/lib/global/brandColors.js`, an internal path of a package we
 * only depend on transitively, and reaching into it would break on any
 * reorganisation. `ramp.test.ts` re-derives these from the `brandWeb` hexes and
 * fails if they ever drift, so the copy cannot go stale silently.
 *
 * Stop 80 sits at L* 44.94 and stop 120 at L* 72.56 — the two figures quoted in
 * the header of `brand.ts`, which is where the orange anchors came from.
 */
const REFERENCE: Record<BrandStop, { l: number; c: number }> = {
  10: { l: 7.03, c: 11.19 },
  20: { l: 12.85, c: 16.59 },
  30: { l: 17.98, c: 20.95 },
  40: { l: 23.72, c: 25.16 },
  50: { l: 29.12, c: 31.3 },
  60: { l: 34.67, c: 36.73 },
  70: { l: 39.19, c: 43.59 },
  80: { l: 44.94, c: 49.75 },
  90: { l: 54.84, c: 52.53 },
  100: { l: 63.66, c: 51.06 },
  110: { l: 68.25, c: 43.74 },
  120: { l: 72.56, c: 38.12 },
  130: { l: 78.33, c: 30.71 },
  140: { l: 84.33, c: 21.51 },
  150: { l: 89.7, c: 13.25 },
  160: { l: 95.49, c: 5.33 },
};

const D65_X = 0.95047;
const D65_Y = 1;
const D65_Z = 1.08883;

// The knee in the CIE Lab transfer function, 6/29.
const LAB_DELTA = 6 / 29;

const HEX_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export type Triplet = [number, number, number];

/**
 * Accepts `#abc` or `#aabbcc` in any case and returns the canonical
 * uppercase six-digit form, or `null` if the input is not a hex colour.
 *
 * Used on anything the user types, so it must never throw.
 */
export function normaliseHex(value: string): string | null {
  const trimmed = value.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;

  const body = trimmed.slice(1);
  const full =
    body.length === 3
      ? body
          .split('')
          .map((char) => char + char)
          .join('')
      : body;

  return `#${full.toUpperCase()}`;
}

export function hexToRgb(hex: string): Triplet {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

export function rgbToHex([r, g, b]: Triplet): string {
  const channel = (value: number) =>
    Math.min(255, Math.max(0, Math.round(value)))
      .toString(16)
      .padStart(2, '0');

  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

/** sRGB 0–255 to linear-light 0–1. */
function toLinear(channel: number): number {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Linear-light 0–1 back to sRGB 0–255, deliberately *unclamped* so the
 *  gamut check below can see how far outside the cube a colour falls. */
function fromLinear(channel: number): number {
  const s = channel <= 0.0031308 ? channel * 12.92 : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
  return s * 255;
}

function rgbToXyz([r, g, b]: Triplet): Triplet {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  return [
    lr * 0.4124564 + lg * 0.3575761 + lb * 0.1804375,
    lr * 0.2126729 + lg * 0.7151522 + lb * 0.072175,
    lr * 0.0193339 + lg * 0.119192 + lb * 0.9503041,
  ];
}

function xyzToRgb([x, y, z]: Triplet): Triplet {
  return [
    fromLinear(x * 3.2404542 + y * -1.5371385 + z * -0.4985314),
    fromLinear(x * -0.969266 + y * 1.8760108 + z * 0.041556),
    fromLinear(x * 0.0556434 + y * -0.2040259 + z * 1.0572252),
  ];
}

function labF(t: number): number {
  return t > LAB_DELTA ** 3 ? Math.cbrt(t) : t / (3 * LAB_DELTA ** 2) + 4 / 29;
}

function labFInverse(t: number): number {
  return t > LAB_DELTA ? t ** 3 : 3 * LAB_DELTA ** 2 * (t - 4 / 29);
}

function xyzToLab([x, y, z]: Triplet): Triplet {
  const fx = labF(x / D65_X);
  const fy = labF(y / D65_Y);
  const fz = labF(z / D65_Z);

  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

function labToXyz([l, a, b]: Triplet): Triplet {
  const fy = (l + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;

  return [D65_X * labFInverse(fx), D65_Y * labFInverse(fy), D65_Z * labFInverse(fz)];
}

/** [L*, C*, h°] — h in degrees, 0–360. */
export function hexToLch(hex: string): Triplet {
  const [l, a, b] = xyzToLab(rgbToXyz(hexToRgb(hex)));
  const hue = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;

  return [l, Math.hypot(a, b), hue];
}

function lchToRgb([l, c, h]: Triplet): Triplet {
  const radians = (h * Math.PI) / 180;
  return xyzToRgb(labToXyz([l, c * Math.cos(radians), c * Math.sin(radians)]));
}

// Half a step of tolerance, because the channel is rounded to an integer anyway.
function isInGamut(rgb: Triplet): boolean {
  return rgb.every((channel) => channel >= -0.5 && channel <= 255.5);
}

/**
 * The most chroma sRGB can hold at a given lightness and hue.
 *
 * Binary search rather than an analytic solution: the sRGB gamut boundary in
 * Lab has no closed form, and 24 halvings of [0, 200] settle well below the
 * precision an 8-bit channel can express.
 */
export function maxChromaInGamut(l: number, h: number): number {
  let low = 0;
  let high = 200;

  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2;
    if (isInGamut(lchToRgb([l, mid, h]))) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return low;
}

/**
 * A sixteen-stop brand ramp built around `keyHex`.
 *
 * The key colour is placed verbatim at whichever stop its lightness is nearest
 * to, and every other stop takes the reference ramp's lightness at the key's
 * hue, with chroma scaled so the key's own saturation carries through the whole
 * ramp (then clipped to whatever sRGB can actually represent there).
 *
 * Pinning matters: it means the colour the user chose appears literally in the
 * ramp rather than being approximated, which is exactly why `#FD991D` sits at
 * stop 120 of the hand-authored orange.
 *
 * An achromatic input (any grey) yields a grey ramp — chroma scales to zero and
 * the meaningless hue falls out of the maths.
 */
export function generateRamp(keyHex: string): BrandVariants {
  const key = normaliseHex(keyHex);
  if (!key) {
    throw new Error(`generateRamp expected a hex colour, received "${keyHex}".`);
  }

  const [keyL, keyC, keyH] = hexToLch(key);

  let pinned: BrandStop = BRAND_STOPS[0];
  for (const stop of BRAND_STOPS) {
    if (Math.abs(REFERENCE[stop].l - keyL) < Math.abs(REFERENCE[pinned].l - keyL)) {
      pinned = stop;
    }
  }

  const chromaScale = keyC / REFERENCE[pinned].c;

  const entries = BRAND_STOPS.map((stop) => {
    if (stop === pinned) return [stop, key] as const;

    const { l, c } = REFERENCE[stop];
    const chroma = Math.min(c * chromaScale, maxChromaInGamut(l, keyH));

    return [stop, rgbToHex(lchToRgb([l, chroma, keyH]))] as const;
  });

  return Object.fromEntries(entries) as BrandVariants;
}

/** WCAG 2.x contrast for normal-size text. */
export const AA_CONTRAST = 4.5;

/** WCAG 2.x relative luminance. Not the same thing as CIE L*. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as Triplet;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio, 1:1 to 21:1. Order of arguments does not matter. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);

  return (lighter + 0.05) / (darker + 0.05);
}
